// Talks to the server auth functions. The login session lives in a secure,
// HttpOnly cookie the browser sends automatically — this code never sees it.
// We keep only a non-secret cached user id in localStorage so other code (e.g.
// the rate-limit hint) can read "who's logged in" synchronously.

import { User, clearData } from './storage';

const UID_KEY = 'dms_uid';
export function setCachedUserId(id: string | null) {
  if (id) localStorage.setItem(UID_KEY, id);
  else localStorage.removeItem(UID_KEY);
}
export function getCachedUserId(): string | null {
  return localStorage.getItem(UID_KEY);
}

async function post(path: string, body?: any): Promise<any> {
  const res = await fetch(`/.netlify/functions/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Something went wrong. Please try again.');
  return data;
}

export async function signup(input: { firstName: string; lastName: string; email: string; password: string; school?: string; joinCode?: string; role?: string; gradeBand?: string }): Promise<User> {
  const { user } = await post('auth-signup', input);
  setCachedUserId(user.id);
  return user;
}

// ---- Owner-only school-license admin (Stage 5) ----
export interface LicenseMember { id: string; name: string; email: string; }
export interface License { id: string; name: string; seatCount: number; joinCode: string; expiresAt: string | null; seatsUsed: number; members: LicenseMember[]; }

export async function adminListLicenses(): Promise<License[]> {
  const { licenses } = await post('admin-licenses', { action: 'list' });
  return licenses || [];
}
export async function adminCreateLicense(input: { name: string; seatCount: number; expiresAt: string }): Promise<void> {
  await post('admin-licenses', { action: 'create', ...input });
}
export async function adminUpdateLicense(input: { licenseId: string; seatCount?: number; expiresAt?: string }): Promise<void> {
  await post('admin-licenses', { action: 'update', ...input });
}
export async function adminRemoveTeacher(teacherId: string): Promise<void> {
  await post('admin-licenses', { action: 'removeTeacher', teacherId });
}

export interface UsageRow { id: string; name: string; email: string; workups: number; }
export interface TestEmailResult { ok: boolean; to: string; from: string; hasKey: boolean; error?: string; }
export async function adminSendTestEmail(): Promise<TestEmailResult> {
  return post('admin-test-email', {});
}

export async function adminGetUsage(period?: string): Promise<{ period: string; usage: UsageRow[] }> {
  const { period: p, usage } = await post('admin-usage', period ? { period } : {});
  return { period: p, usage: usage || [] };
}

export async function login(email: string, password: string): Promise<User> {
  const { user } = await post('auth-login', { email, password });
  setCachedUserId(user.id);
  return user;
}

export async function logout(): Promise<void> {
  await post('auth-logout').catch(() => {});
  setCachedUserId(null);
  clearData();
}

export async function me(): Promise<User | null> {
  try {
    const res = await fetch('/.netlify/functions/auth-me', { credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    if (data?.user) {
      setCachedUserId(data.user.id);
      return data.user as User;
    }
  } catch {
    /* fall through */
  }
  setCachedUserId(null);
  return null;
}

export async function requestReset(email: string): Promise<void> {
  await post('auth-request-reset', { email });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await post('auth-reset-password', { token, password });
}

// Start a Stripe Checkout Session on the server and return its URL to redirect to.
export async function createCheckout(plan: 'monthly' | 'annual'): Promise<string> {
  const { url } = await post('create-checkout', { plan });
  return url;
}

// Open Stripe's Customer Portal (update card, invoices, cancel) and return its URL.
// Throws an Error whose .code is 'no_customer' when Stripe has no subscription
// for this account (the caller then offers the cancellation escape hatch).
export async function createPortalSession(): Promise<string> {
  const res = await fetch('/.netlify/functions/create-portal-session', {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: any = new Error(data?.error || 'Could not open the billing portal.');
    err.code = data?.code;
    throw err;
  }
  return data.url;
}

// Escape hatch: ask the owner to cancel by hand when the portal can't be opened.
export async function requestCancellation(): Promise<void> {
  await post('request-cancellation');
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await post('auth-change-password', { currentPassword, newPassword });
}

export async function changeEmail(password: string, newEmail: string): Promise<User> {
  const { user } = await post('auth-change-email', { password, newEmail });
  return user;
}

// Public "Contact Us" form → emails the owner with reply-to set to the sender.
export async function sendContact(input: { name: string; email: string; message: string }): Promise<void> {
  await post('contact', input);
}

// Public "Request a school license" form (the /schools page).
export interface LicenseRequestInput {
  schoolName: string; district?: string; state?: string; studentCount?: string;
  contactName: string; role?: string; workEmail: string; phone?: string; situation?: string;
}
export async function requestLicense(input: LicenseRequestInput): Promise<void> {
  await post('license-request', input);
}

// ---- Owner-only leads admin ----
export interface Lead {
  id: string; schoolName: string; district: string | null; state: string | null;
  studentCount: string | null; contactName: string; role: string | null;
  workEmail: string; phone: string | null; situation: string | null;
  status: string; createdAt: string;
}
export async function adminListLeads(): Promise<Lead[]> {
  const { leads } = await post('admin-leads', { action: 'list' });
  return leads || [];
}
export async function adminUpdateLeadStatus(id: string, status: string): Promise<void> {
  await post('admin-leads', { action: 'updateStatus', id, status });
}
// Send the one-page quote to a lead and mark it quoted. Optionally correct the
// student count first (which also sets the tier).
export async function adminSendQuote(id: string, studentCount?: string): Promise<{ status: string; tier: string; price: number }> {
  return post('admin-leads', { action: 'sendQuote', id, ...(studentCount ? { studentCount } : {}) });
}

// ---- Public accept flow (the /accept page from the quote email) ----
export interface AcceptSummary {
  schoolName: string; contactName: string | null; tierLabel: string; price: number; priceLabel: string; alreadyActive?: boolean;
}
export async function acceptSummary(leadId: string): Promise<AcceptSummary> {
  const res = await fetch(`/.netlify/functions/school-accept?lead=${encodeURIComponent(leadId)}`, { credentials: 'same-origin' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Could not load this quote.');
  return data;
}
export async function acceptSubmit(input: { lead: string; billingName: string; billingEmail: string; poNumber?: string }): Promise<void> {
  await post('school-accept', input);
}

// ---- Owner-only outreach admin (read-only view of the outreach_contacts table) ----
export interface OutreachContact {
  email: string; firstName: string | null; lastName: string | null; title: string | null;
  organization: string | null; state: string | null; segment: string | null; source: string | null;
  status: string; lastSentAt: string | null; nextSendAt: string | null; notes: string | null; createdAt: string;
}
export async function adminListOutreach(status?: string): Promise<OutreachContact[]> {
  const { contacts } = await post('admin-outreach', status ? { status } : {});
  return contacts || [];
}

// In-app feedback (1–5 rating + optional comment) from a signed-in teacher.
export async function sendFeedback(input: { rating: number; comment?: string }): Promise<void> {
  await post('feedback', input);
}

// Permanently delete the signed-in teacher's account and all of her data.
// Requires her password. On success the session cookie is cleared server-side.
export async function deleteAccount(password: string): Promise<void> {
  await post('delete-account', { password });
  setCachedUserId(null);
  clearData();
}

// Best-effort one-time migration of this device's old localStorage data into the
// new server account. Silent on failure — it must never block getting into the app.
export async function migrateLocalData(): Promise<void> {
  try {
    const oldUid = localStorage.getItem('dms_current_user');
    if (!oldUid) return;
    const students = JSON.parse(localStorage.getItem(`dms_students_${oldUid}`) || '[]');
    const burnout = JSON.parse(localStorage.getItem(`dms_burnout_${oldUid}`) || '[]');
    const strategies = JSON.parse(localStorage.getItem(`dms_strategies_${oldUid}`) || '[]');
    const ratings = JSON.parse(localStorage.getItem(`dms_daily_ratings_${oldUid}`) || '[]');
    const debriefs = JSON.parse(localStorage.getItem(`dms_debriefs_${oldUid}`) || '[]');
    const hasAny = [students, burnout, strategies, ratings, debriefs].some((a) => Array.isArray(a) && a.length);
    if (!hasAny) return;
    await post('migrate', { students, burnout, strategies, ratings, debriefs });
  } catch {
    /* ignore — migration is best-effort */
  }
}
